import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { Component, inject, effect, signal, ChangeDetectorRef, OnInit } from '@angular/core';
import { HuntQueryCardComponent } from '../mode/components/queries';
import { HuntEditorComponent } from '../mode/components/hunt-editor';
import { HuntContextService } from '../services/hunt-context.service';
import { FormsModule } from '@angular/forms'; // ✅ make sure this is imported
import { toObservable } from '@angular/core/rxjs-interop';
import {
  TypographyComponent,
  BadgeComponent,
  TagComponent,
  ButtonComponent,
  BreadcrumbListComponent,
  BreadcrumbItem,
  SelectComponent
} from '@hyperkit';
import { DefaultViewComponent, QuerySelectedViewComponent } from './components';
import { Tool } from '@cyborg-monorepo/hunter/common';
export type QueryClickEvent =
  | { type: 'query'; tool: Tool; index: number }
  | { type: 'pivot'; tool: Tool; queryIndex: number; pivotIndex: number };


  function buildQueryKey(toolId: string, queryIndex: number, pivotIndex?: number): string {
    return pivotIndex !== undefined
      ? `${toolId}::${queryIndex}::${pivotIndex}`
      : `${toolId}::${queryIndex}`;
  }
  
@Component({
  standalone: true,
  selector: 'cyborg-monorepo-hunt-package-details',
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    TypographyComponent,
    HuntEditorComponent,
    BadgeComponent,
    TagComponent,
    DefaultViewComponent,
    QuerySelectedViewComponent,
    HuntQueryCardComponent,
    BreadcrumbListComponent,
    SelectComponent,
    FormsModule
  ],
  templateUrl: './hunt-package-details.component.html',
  styleUrl: './hunt-package-details.component.scss',
})
export class HuntPackageDetailsComponent {
  title = 'Hunts Package Details Page';
  private cdr = inject(ChangeDetectorRef);
  readonly hunt = inject(HuntContextService);

  // 🍃 NEW computed references from `hunt.editor`
  readonly environments = this.hunt.editor.getAllEnvironments;
  readonly selectedEnvironment = this.hunt.editor.selectedEnvironment;
  readonly activeTools = this.hunt.editor.getToolsBySelectedEnvironment;
  readonly activeHuntPackage = this.hunt.editor.activePackage;
  readonly isHuntComplete = this.hunt.query.isComplete;
  
  
  public breadcrumbs = signal<BreadcrumbItem[]>([]);  // Initialize with empty array

  onSelectEnvironmentById(selectedId: string | number): void {
    const env = this.environments().find(e => e.id === selectedId);
    if (env) {
      this.hunt.editor.setEnvironment(env);
    }
  }
  
  trackByTool(index: number, tool: Tool) {
    return tool.id;
  }

  onQuerySelect(event: QueryClickEvent) {
    const queryKey = event.type === 'query'
      ? buildQueryKey(event.tool.id, event.index)
      : buildQueryKey(event.tool.id, event.queryIndex, event.pivotIndex);
  
    console.log('queryKey selected', queryKey);
  
    // Force bounce to retrigger tab switch logic
    this.hunt.ui.queryViewTab.set(null as any); // invalidate
    queueMicrotask(() => {
      this.hunt.ui.selectedQueryId.set(queryKey);
      this.hunt.ui.queryViewTab.set('overview');
    });
  }
  
  constructor() {
    const route = inject(ActivatedRoute);
    const resolvedHunt = route.snapshot.data['hunt'];
  
    if (resolvedHunt) {
      this.hunt.state.updateHunt(resolvedHunt);
      this.hunt.editor.load(resolvedHunt); 
    }

    // 🧭 Set breadcrumbs based on editor package
    effect(() => {
      this.breadcrumbs.set([
        { text: 'Hunt', link: '/hunt/hunts' },
        {
          text: this.hunt.state.name(),
          link: `/hunt-redesign/${this.hunt.state.id()}/workbench`,
        },
        {
          text: this.hunt.editor.activePackage()?.title ?? 'Untitled Package',
          selected: true,
        },
      ]);
    });
  

    // ✅ Delay environment init until hunt + envs are hydrated
    effect(() => {
      const hunt = this.hunt.editor.hunt();
      const envs = this.environments();
      const selectedEnv = this.selectedEnvironment();

      if (hunt && envs?.length && !selectedEnv) {
        this.hunt.editor.setEnvironment(envs[0]);
      }
    });

    this.hunt.editor.triggerUpdate$.subscribe(() => {
      console.log('🔁 Manual update triggered: package details');
      this.cdr.detectChanges(); // ✅ Triggers view update
    });
      

    // 🔁 Force re-render when a new query is selected
    effect(() => {
      this.hunt.ui.selectedQueryId(); // signal trigger
      queueMicrotask(() => this.cdr.detectChanges()); // manually re-render
    });


    
    // 🧬 Confirm hydration + context
    effect(() => {
      console.log('🧬 Hydration Check:');
      console.log('📦 packageId:', this.hunt.editor.selectedPackageId());
      console.log('📦 activePackage:', this.hunt.editor.activePackage());
      console.log('📝 editor.hunt:', this.hunt.editor.hunt());
    });

    // 🌐 UI state + environment logs
    effect(() => {
      console.log('🌐 UI State Changed:');
      console.log('🔁 Environment:', this.selectedEnvironment());
      console.log('🔧 Tools:', this.activeTools());
      console.log('👉 selectedQuery:', this.hunt.queryLogic.selectedQuery());
      console.log('👉 selectedTool:', this.hunt.queryLogic.selectedTool());
    });
  }
}
